/**
 * Issue #248 — one-shot hydration of a focused deep link (extended to college
 * football by #539).
 *
 * A Position Tracker graph chip encodes the exact market it already knows into
 * a `/dashboard?focus=market&…` URL. This hook is the Value Dashboard's side of
 * that contract: it watches the live rows for the ONE exact market the URL
 * names, opens the existing trade panel through the dashboard's own
 * `handleTradeClick`, and does so at most once.
 *
 * It resolves MONEYLINE targets itself, against `games`. Spread and total
 * targets are resolved by the Spread/Total board and reported back through
 * `externalResolution` — see that type. Either way the state machine below is
 * the only one, so both kinds obey identical fail-closed rules.
 *
 * Deliberately narrow: parsing and matching live in the pure routing module
 * (`src/lib/routing/focusedDashboard.ts`). What is here — and nowhere else —
 * is the one-shot state and the connected-timeout.
 *
 * Fail-closed rules this encodes (see the #248 plan's hydration state table):
 *
 *  - The unavailable clock starts when the feed FIRST reports connected, not
 *    at page mount. A slow connect must not burn the trader's window; a
 *    reconnect must not extend it either, so the bound is kept as an absolute
 *    deadline rather than a restartable timer.
 *  - Zero matches is not a verdict — the row may simply not have arrived yet,
 *    so a miss keeps waiting. A contradiction the feed can never resolve
 *    (ambiguous, or a Polymarket ticker with no native-YES identity) fails
 *    closed AT ONCE: waiting would only delay the same answer.
 *  - After the timeout the focus is FROZEN unavailable. A row that arrives
 *    late must never surprise a trader by opening an order ticket.
 *  - Opening is idempotent, keyed by the parsed focus identity and guarded
 *    through a ref written BEFORE `openMatch` is called. `openMatch` closes
 *    over `games`, which is re-derived on every feed tick, so callback
 *    identity is worthless as a guard — and React Strict Mode re-runs the
 *    effect on mount regardless.
 *
 * The hook never touches the URL. Dismissal is local so reload and sharing
 * still reproduce the original focus.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FOCUSED_DASHBOARD_WAIT_MS,
  matchFocusedDashboardTarget,
  type FocusedDashboardTarget,
  type FocusedVenue,
  type ParsedDashboardFocus,
} from '@/lib/routing/focusedDashboard';
import type { GameRow, TeamSide } from './types';

/** The one stable notice for every failed focus — invalid, explicitly
 *  unavailable, ambiguous, contradictory, or timed out. One sentence of what
 *  happened, one of what did NOT happen: a trader who followed a link has to
 *  be told, unambiguously, that no order ticket was opened.
 *
 *  Deliberately sport-neutral since #539 added CFB. Naming the sports here was
 *  already the weaker wording — the trader followed a link from a row whose
 *  sport they can see — and it turned a copy string into a fourth place the
 *  routed-sport list has to be kept in sync. */
export const FOCUSED_DASHBOARD_UNAVAILABLE_NOTICE =
  'Market unavailable: this link did not match one exact market. No order ticket was opened.';

export type FocusHydrationPhase = 'none' | 'pending' | 'opened' | 'unavailable' | 'dismissed';

/**
 * The verdict for a target this hook cannot resolve itself (#539).
 *
 * Moneyline targets are matched HERE, against `games`. Spread and total targets
 * cannot be: their rows come from `getGameEvents`, which — unlike `getGames` —
 * is NOT cached, so calling it a second time from the dashboard would rebuild
 * every event on every feed tick. The board that already holds those rows
 * memoized resolves them and reports the verdict back through this field.
 *
 * What deliberately does NOT move with it is the state machine. One-shot,
 * connected-timeout, frozen-unavailable and dismissal stay here so a line
 * target obeys exactly the same fail-closed rules a moneyline target does —
 * the alternative being two hydration paths that drift apart.
 *
 *  - `searching` — no match YET. Same standing as a moneyline miss: the row may
 *    still be in flight, so the clock keeps running rather than failing early.
 *  - `opened`    — resolved and the panel is open. The resolver opens it, so
 *    this hook does NOT call `openMatch` for these targets.
 *  - `unavailable` — a contradiction waiting cannot fix.
 */
export type ExternalFocusResolution = 'searching' | 'opened' | 'unavailable';

export interface UseFocusedDashboardHydrationInput {
  focus: ParsedDashboardFocus;
  connected: boolean;
  games: readonly GameRow[];
  polymarketNativeYesTokenId?: string;
  /** Present ONLY for targets resolved elsewhere (spread/total). When set, the
   *  internal `games` matcher is bypassed entirely — never consulted as a
   *  fallback, because `games` holds moneyline rows and a line ticker missing
   *  from them means nothing. */
  externalResolution?: ExternalFocusResolution;
  openMatch: (
    match: { gameKey: string; teamSide: TeamSide; venue: FocusedVenue },
    target: FocusedDashboardTarget,
  ) => void;
}

export interface FocusHydrationState {
  phase: FocusHydrationPhase;
  message: string | null;
  dismiss: () => void;
}

/** The one-shot key. `identity` is the parsed focus query's own string form,
 *  so a different focus URL resets the state and a re-render with an equal
 *  query does not. `kind` is folded in because an `invalid` and an
 *  `unavailable` query can serialize identically only if they are the same
 *  string, but keeping the kind here makes the reset rule total. */
function focusKey(focus: ParsedDashboardFocus): string {
  return focus.kind === 'none' ? '' : `${focus.kind}:${focus.identity}`;
}

/** `invalid` and `unavailable` share the `unavailable` PHASE: the state table
 *  gives them the same user-visible outcome (stable notice, never match), and
 *  a separate phase would only invite a caller to render them differently. */
function initialPhase(focus: ParsedDashboardFocus): FocusHydrationPhase {
  if (focus.kind === 'none') return 'none';
  if (focus.kind === 'market') return 'pending';
  return 'unavailable';
}

interface OneShot {
  key: string;
  phase: FocusHydrationPhase;
}

export function useFocusedDashboardHydration({
  focus,
  connected,
  games,
  polymarketNativeYesTokenId,
  externalResolution,
  openMatch,
}: UseFocusedDashboardHydrationInput): FocusHydrationState {
  const key = focusKey(focus);
  const initial = initialPhase(focus);
  const [tracked, setTracked] = useState<OneShot>(() => ({ key, phase: initial }));

  // The ref — not the state — is the guard the open path reads. State lands a
  // render later, which is exactly the window a Strict Mode effect re-run (or
  // two feed ticks in one commit) would open through.
  const oneShot = useRef<OneShot>(tracked);
  // Absolute deadline, not a duration: a disconnect/reconnect re-arms the
  // timer for the REMAINING time instead of granting a fresh 10 s.
  const deadlineMs = useRef<number | null>(null);

  // A different focus URL resets the one-shot. The RENDERED phase is derived
  // rather than stored, so a new focus never shows the previous focus's
  // verdict for a frame while the reset effect below catches the ref up.
  const phase = tracked.key === key ? tracked.phase : initial;

  // `openMatch` closes over `games` and so changes on every feed tick. Held in
  // a ref and deliberately kept OUT of the effect deps: including it would
  // re-run the match effect on every tick for no gain, and the one-shot guard
  // — not dependency stability — is what makes opening idempotent.
  const openMatchRef = useRef(openMatch);
  useEffect(() => {
    openMatchRef.current = openMatch;
  });

  // Declared BEFORE the timer and match effects on purpose: effects in one
  // commit run in declaration order, so by the time those two read the ref it
  // already belongs to the new focus — the reset costs no wasted commit.
  useEffect(() => {
    if (oneShot.current.key === key) return;
    oneShot.current = { key, phase: initial };
    deadlineMs.current = null;
    setTracked(oneShot.current);
  }, [key, initial]);

  /** Leave `pending` exactly once, and only for the focus that is still
   *  current. Returns whether this call is the one that made the transition —
   *  the open path uses that answer to decide whether to fire `openMatch`. */
  const settle = useCallback((expectedKey: string, next: FocusHydrationPhase): boolean => {
    if (oneShot.current.key !== expectedKey) return false;
    if (oneShot.current.phase !== 'pending') return false;
    oneShot.current = { key: expectedKey, phase: next };
    setTracked(oneShot.current);
    return true;
  }, []);

  // ── The connected-timeout ───────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'pending' || !connected) return;
    if (deadlineMs.current === null) {
      deadlineMs.current = Date.now() + FOCUSED_DASHBOARD_WAIT_MS;
    }
    const remaining = Math.max(0, deadlineMs.current - Date.now());
    const timer = setTimeout(() => {
      settle(key, 'unavailable');
    }, remaining);
    return () => clearTimeout(timer);
  }, [phase, connected, key, settle]);

  // ── The exact-match search ──────────────────────────────────────────
  // Runs on every commit that changes `games` while pending. Matching is
  // gated on `connected` for the same reason the clock is: an absent row on a
  // dead feed is not evidence of anything.
  useEffect(() => {
    if (phase !== 'pending' || !connected) return;
    if (focus.kind !== 'market') return;

    // #539 — a target resolved elsewhere (spread/total). Take the reported
    // verdict and DO NOT fall through to the `games` matcher: `games` holds
    // moneyline rows, so a line ticker's absence from it is not evidence, and
    // treating it as a miss would merely be the same answer arrived at
    // dishonestly. The resolver opens the panel itself, so `openMatch` is not
    // called here — calling it would look up a line ticker in moneyline rows
    // and quietly do nothing.
    if (externalResolution !== undefined) {
      if (externalResolution === 'searching') return; // same standing as a miss
      settle(key, externalResolution === 'opened' ? 'opened' : 'unavailable');
      return;
    }

    const result = matchFocusedDashboardTarget(focus.target, games, {
      polymarketNativeYesTokenId,
    });
    // A miss keeps waiting — the row may still be in flight.
    if (result.kind === 'miss') return;
    if (result.kind === 'match') {
      const opened = settle(key, 'opened');
      if (opened) {
        openMatchRef.current(
          { gameKey: result.gameKey, teamSide: result.teamSide, venue: result.venue },
          focus.target,
        );
      }
      return;
    }
    settle(key, 'unavailable');
  }, [phase, connected, focus, games, polymarketNativeYesTokenId, externalResolution, key, settle]);

  /** Local dismissal — the panel closed, or the trader took the board
   *  somewhere else. Never rewrites the query: reloading the same URL runs
   *  hydration again, which is what makes the link shareable. */
  const dismiss = useCallback(() => {
    const { key: currentKey, phase } = oneShot.current;
    if (phase === 'none' || phase === 'dismissed') return;
    oneShot.current = { key: currentKey, phase: 'dismissed' };
    setTracked(oneShot.current);
  }, []);

  return useMemo(
    () => ({
      phase,
      message: phase === 'unavailable' ? FOCUSED_DASHBOARD_UNAVAILABLE_NOTICE : null,
      dismiss,
    }),
    [phase, dismiss],
  );
}
